/**
 * 「运行与安全 B」五页共用的小控件：筛选栏输入框与下拉、状态胶囊、「第 x / y 页」翻页、时间显示。
 *
 * 背景：Alice 的筛选栏、状态标签是 HTML 里的内联样式，没有对应的类；视频、音频、配置变更页都要用同一套。
 * 设计意图：把 Alice 的内联样式原样收进组件，页面只写业务；颜色沿用 Alice 的状态色（kit 里没有对应类名）。
 *   G5 的共享控件（kit-runtime.tsx）开工时还没有，这里自己写，不等它。
 * 关键约束：不改 kit.tsx 的既有导出；需要的新控件都放在本文件。
 */
import type { CSSProperties, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'

export const fieldStyle: CSSProperties = {
  padding: '8px 10px',
  fontSize: 13,
  background: 'var(--bg-input)',
  border: '1px solid var(--border-medium)',
  borderRadius: 6,
  color: 'var(--text-primary)',
  fontFamily: 'var(--font)',
}

/** 筛选栏输入框；width 单位 px，回车触发 onEnter。 */
export function ToolInput({
  width,
  onEnter,
  style,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { width?: number; onEnter?: () => void }) {
  return (
    <input
      {...rest}
      style={{ ...fieldStyle, width, ...style }}
      onKeyDown={(e) => {
        rest.onKeyDown?.(e)
        // 输入法组合中的回车是选词，不触发查询
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) onEnter?.()
      }}
    />
  )
}

export function ToolSelect({
  options,
  style,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { options: { value: string; label: string }[] }) {
  return (
    <select {...rest} style={{ ...fieldStyle, padding: '8px 28px 8px 10px', ...style }}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

/** Alice 的状态胶囊（.video-badge），颜色成对给出：文字色与底色。 */
export type PillTone = readonly [color: string, background: string]

export const PILL = {
  blue: ['#1d4ed8', '#dbeafe'],
  purple: ['#6d28d9', '#ede9fe'],
  amber: ['#b45309', '#fef3c7'],
  green: ['#15803d', '#dcfce7'],
  red: ['#b91c1c', '#fee2e2'],
  gray: ['#64748b', '#f1f5f9'],
  sky: ['#0369a1', '#e0f2fe'],
} as const satisfies Record<string, PillTone>

export function Pill({ tone, title, children }: { tone: PillTone; title?: string; children: ReactNode }) {
  return (
    <span className="video-badge" style={{ color: tone[0], background: tone[1] }} title={title}>
      {children}
    </span>
  )
}

/** 「第 x / y 页」加上一页、下一页（Alice 视频、音频、配置变更页的翻页样式）。 */
export function PageNav({
  page,
  pages,
  label,
  prev,
  next,
  onPage,
}: {
  page: number
  pages: number
  label: string
  prev: string
  next: string
  onPage: (page: number) => void
}) {
  return (
    <>
      <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginRight: 8 }}>{label}</span>
      <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        {prev}
      </button>
      <button className="btn btn-secondary btn-sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        {next}
      </button>
    </>
  )
}

/** 整页空状态行：表格里 colSpan 撑满。 */
export function EmptyRow({ cols, children }: { cols: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={cols} style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)' }}>
        {children}
      </td>
    </tr>
  )
}

/** 卡片里的一行省略文字。 */
export const ellipsis: CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

/** 指标卡副文字的警示色（毛利为负时）。 */
export function subColor(negative: boolean): CSSProperties {
  return negative ? { color: 'var(--error-text)' } : {}
}

/** 页内的小节卡片：.ops-section-card + 标题与副标题。 */
export function Section({ title, copy, children, onToggle, toggle }: { title: ReactNode; copy?: ReactNode; children: ReactNode; onToggle?: () => void; toggle?: ReactNode }) {
  return (
    <div className="ops-section-card">
      <div className="ops-section-head" style={onToggle ? { cursor: 'pointer' } : undefined} onClick={onToggle}>
        <div>
          <div className="ops-section-title">{title}</div>
          {copy && <div className="ops-section-copy">{copy}</div>}
        </div>
        {toggle}
      </div>
      {children}
    </div>
  )
}
